import { useCallback, useEffect, useMemo, useRef } from 'react'

import {
  useAddFcmTokenToSubscriberSettings,
  useRemoveFcmTokenFromSubscriberSettings,
  useSubscriberSettings,
} from '@tribeplatform/react-sdk/hooks'
import { noop } from '@tribeplatform/react-ui-kit/hooks'

import { useLogin } from '../../hooks/useLogin.js'

import { DesktopNotification } from './types.js'
import { useFcmToken } from './useFcmToken.js'
import { useNotificationStatus } from './useNotificationStatus.js'

/**
 * Persists this device's last-registered FCM token so a token rotated while the
 * app was closed can still evict the stale one from subscriber settings on the
 * next load (otherwise the old token orphans in `fcmDeviceTokens`).
 */
export const FCM_DEVICE_TOKEN_STORAGE_KEY = 'bm:fcm-device-token'

const readStoredDeviceToken = (): string | null => {
  if (typeof localStorage === 'undefined') {
    return null
  }
  try {
    return localStorage.getItem(FCM_DEVICE_TOKEN_STORAGE_KEY)
  } catch {
    return null
  }
}

const writeStoredDeviceToken = (token: string): void => {
  if (typeof localStorage === 'undefined') {
    return
  }
  try {
    localStorage.setItem(FCM_DEVICE_TOKEN_STORAGE_KEY, token)
  } catch {
    // Ignore (private mode / storage disabled).
  }
}

const clearStoredDeviceToken = (): void => {
  if (typeof localStorage === 'undefined') {
    return
  }
  try {
    localStorage.removeItem(FCM_DEVICE_TOKEN_STORAGE_KEY)
  } catch {
    // Ignore (private mode / storage disabled).
  }
}

/**
 * Orchestrates browser/PWA push notifications and produces the
 * `DesktopNotification` context value, composing `useNotificationStatus`
 * (permission) and `useFcmToken` (device token). The token is only fetched from
 * a user gesture (`enable`) — except a read-only mount fetch when permission is
 * already `granted`, used purely to compute whether this device is already
 * subscribed. Used only by the provider; consumers read the value via
 * `useContext(DesktopNotificationContext)`.
 */
export const useDesktopNotification = (): DesktopNotification => {
  const { isLoggedIn } = useLogin()
  const { data: settings } = useSubscriberSettings({
    useQueryOptions: { enabled: isLoggedIn },
  })
  const { mutateAsync: addFcmToken } = useAddFcmTokenToSubscriberSettings()
  const { mutateAsync: removeFcmToken } =
    useRemoveFcmTokenFromSubscriberSettings()

  const {
    status,
    context,
    recompute: recomputeStatus,
  } = useNotificationStatus()
  const { token: fcmToken, initialize, fetchToken, clearToken } = useFcmToken()

  // This device's last-registered token, seeded from localStorage so rotation
  // cleanup works across sessions (not just within one). Updated whenever a
  // fresh token is fetched.
  const previousFcmTokenRef = useRef<string | null>(readStoredDeviceToken())

  // True while `enable()` is driving the grant flow. The permission-change
  // listener observes the same grant and would otherwise fire a duplicate
  // fetchToken/addFcmToken pass, so it defers to `enable()` while this is set.
  const promptInFlightRef = useRef(false)

  const enabled = !!fcmToken && !!settings?.fcmDeviceTokens?.includes(fcmToken)

  // Check if a logged in user is already subscribed to notifications.
  useEffect(() => {
    if (!isLoggedIn) {
      return
    }

    initialize()
    const next = recomputeStatus()
    if (next.status === 'granted') {
      fetchToken().catch(noop)
    }
  }, [fetchToken, initialize, isLoggedIn, recomputeStatus])

  // Listen for browser notification permission changes.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
      return undefined
    }

    let active = true
    let permissionStatus: PermissionStatus | undefined
    const handleChange = () => {
      // `enable()` owns the grant flow (fetchToken + addFcmToken); skip here
      // while its prompt is in flight to avoid a duplicate registration pass.
      if (promptInFlightRef.current) {
        return
      }
      const next = recomputeStatus()
      if (next.status === 'granted') {
        fetchToken().catch(noop)
      } else {
        // Permission revoked live: evict this device's token from settings.
        const previousToken = previousFcmTokenRef.current
        if (previousToken) {
          removeFcmToken({ token: previousToken }).catch(noop)
          previousFcmTokenRef.current = null
          clearStoredDeviceToken()
        }
        clearToken()
      }
    }

    navigator.permissions
      .query({ name: 'notifications' })
      .then(result => {
        // The effect may have cleaned up before this resolved; bail so we don't
        // attach a listener that the cleanup below can no longer remove.
        if (!active) {
          return
        }
        permissionStatus = result
        result.addEventListener('change', handleChange)
      })
      .catch(noop)

    return () => {
      active = false
      permissionStatus?.removeEventListener('change', handleChange)
    }
  }, [clearToken, fetchToken, recomputeStatus, removeFcmToken])

  // Evict this device's previously-registered token whenever FCM issues a new
  // one — covers both in-session rotation and a token rotated across sessions
  // (the previous value is read from localStorage on mount). Gated on a real
  // fetched token so the transient `null` before the mount fetch never removes
  // a still-valid subscription.
  useEffect(() => {
    if (!fcmToken) {
      return
    }
    const previousToken = previousFcmTokenRef.current
    if (previousToken && previousToken !== fcmToken) {
      removeFcmToken({ token: previousToken }).catch(noop)
    }
    previousFcmTokenRef.current = fcmToken
    writeStoredDeviceToken(fcmToken)
  }, [fcmToken, removeFcmToken])

  // Enable browser notification. Returns:
  // - true if the notification is enabled
  // - false if the notification is disabled
  // - throws an error if notifications failed to be enabled (e.g. invalid API key)
  const enable = useCallback(async (): Promise<boolean> => {
    initialize()
    // Suppress the permission-change listener for this grant; see promptInFlightRef.
    promptInFlightRef.current = true
    try {
      if (
        // If the Notification API is available
        typeof Notification !== 'undefined' &&
        // And permissions have not been granted OR rejected
        Notification.permission === 'default'
      ) {
        const result = await Notification.requestPermission()
        recomputeStatus()
        if (result !== 'granted') {
          // recomputeStatus will set the status to 'denied' and a warning will be displayed
          return false
        }
      } else {
        recomputeStatus()
        if (
          // If notifications are not available on this device
          typeof Notification === 'undefined' ||
          // Or permissions were previously denied
          Notification.permission !== 'granted'
        ) {
          return false
        }
      }

      // At this point, we should be able to get a valid token
      const token = await fetchToken()
      if (!token) {
        // Triggers an error toast downstream
        throw new Error('Unable to obtain a push notification token.')
      }

      await addFcmToken({ token })

      // Status should have been recomputed already, but if notifications have somehow
      // been disabled after these async operations, we can recompute again just in case
      recomputeStatus()

      // If none of the above exited early, we successfully enabled notifications
      return true
    } finally {
      promptInFlightRef.current = false
    }
  }, [addFcmToken, fetchToken, initialize, recomputeStatus])

  const disable = useCallback(async (): Promise<void> => {
    if (!fcmToken) {
      return
    }
    await removeFcmToken({ token: fcmToken })
  }, [fcmToken, removeFcmToken])

  return useMemo<DesktopNotification>(
    () => ({ status, context, enabled, enable, disable }),
    [status, context, enabled, enable, disable],
  )
}
